import React, { useMemo } from 'react';

import { Box, Chip, Typography } from '@mui/material';
import { makeStyles } from 'tss-react/mui';
import { L10nHTML, useIsMobile } from '@uplift/hooks';

import { Button } from '../../../Button';
import { StackableCard } from '../../../StackableCard';
import { useColorStyles } from './DashboardActionItem.styles';

export type ActionItemColor =
  | 'primary'
  | 'secondary'
  | 'danger'
  | 'warning'
  | 'info'
  | 'success'
  | 'default'
  | 'orange'
  ;

export type ActionItemButton = {
  text: React.ReactNode;
  handleClick: React.MouseEventHandler<HTMLElement>;
};

export type BaseDashboardActionItemProps = {
  icon: React.ReactElement;
  title: React.ReactNode;
  actionButton?: React.ReactNode;
  action?: ActionItemButton;
  badge?: React.ReactNode;
  body?: React.ReactNode;
  bodyClassName?: string;
  color?: ActionItemColor;
  details?: React.ReactNode;
  dismiss?: ActionItemButton;
  expandTextContainer?: React.ReactNode;
  handleActionItemClick?: React.MouseEventHandler<HTMLElement>;
  className?: string;
  testId?: string;
};

export const BaseDashboardActionItem: React.FC<BaseDashboardActionItemProps> = ({
  handleActionItemClick,
  actionButton,
  color = 'default',
  bodyClassName,
  className,
  testId = 'dash-action-item',
  ...props
}) => {
  const isMobile = useIsMobile('sm');
  const { classes: colors } = useColorStyles();
  const { classes: layoutClasses, cx } = useBaseStyles({
    shouldTopAlign: Boolean(props.body || props.children)
  });

  const colorMapping: Record<ActionItemColor, string> = {
    'primary': colors.colorPrimary,
    'secondary': colors.colorSecondary,
    'danger': colors.colorDanger,
    'warning': colors.colorWarning,
    'info': colors.colorInfo,
    'success': colors.colorSuccess,
    'default': colors.colorDefault,
    'orange': colors.colorOrange,
  };

  const badge = useMemo(() => {
    if (!props.badge) {
      return null;
    }

    if (typeof props.badge === 'string') {
      return (
        <Box>
          <Chip
            className={layoutClasses.chip}
            label={props.badge}
            size="small"
          />
        </Box>
      );
    }

    return (
      <Box>
        {props.badge}
      </Box>
    );
  }, [ layoutClasses.chip, props.badge ]);

  return (
    (
      <StackableCard className={cx(layoutClasses.parent, className)} onClick={handleActionItemClick} data-testid={testId}>
        <Box className={layoutClasses.icon}>
          <Box className={cx(layoutClasses.iconWrapper, colorMapping[color])}>
            {props.icon}
          </Box>
          {isMobile ? badge : null}
        </Box>
        <Box className={layoutClasses.centerColumn}>
          <Box className={layoutClasses.title}>
            <Typography variant="body1">
              {renderText(props.title)}
            </Typography>
            {props.details && (
              <Typography variant="body1" color="textSecondary">
                {renderText(props.details)}
              </Typography>
            )}
          </Box>
          {props.body && (
            <Box className={bodyClassName}>
              {renderText(props.body)}
            </Box>
          )}
          {props.expandTextContainer && (
            <Box className={layoutClasses.expandContainer}>
              {props.expandTextContainer}
            </Box>
          )}
        </Box>
        <Box className={layoutClasses.actions}>
          {actionButton && actionButton}
          {props.action && (
            <Button
              variant="contained"
              color="secondary"
              onClick={props.action.handleClick}
              data-testid="dash-action-item-action-btn"
            >
              {props.action.text}
            </Button>
          )}
          {props.dismiss && (
            <Button
              variant="outlined"
              onClick={props.dismiss.handleClick}
              data-testid="dash-action-item-dismiss-btn"
            >
              {props.dismiss.text}
            </Button>
          )}
        </Box>
        {props.children && (
          <Box className={layoutClasses.collapse}>
            {props.children}
          </Box>
        )}
      </StackableCard>
    )
  );
};

type StyleProps = {
  shouldTopAlign: boolean;
};

const useBaseStyles = makeStyles<StyleProps>({ name: 'ActionItem' })((theme, { shouldTopAlign }) => ({
  parent: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    padding: theme.spacing(3, 2),
    width: '100%',
    [theme.breakpoints.up('sm')]: {
      display: 'grid',
      gridColumnGap: theme.spacing(2),
      gridTemplateColumns: '48px 1fr 144px',
      gridTemplateRows: 'minmax(auto, max-content) auto',
      padding: theme.spacing(3),
    }
  },
  icon: {
    [theme.breakpoints.down('sm')]: {
      alignItems: 'center',
      display: 'flex',
      justifyContent: 'space-between',
      marginBottom: theme.spacing(1),
    },
    [theme.breakpoints.up('sm')]: {
      alignItems: shouldTopAlign ? 'start' : 'center',
      display: 'flex',
      gridArea: '1 / 1 / 2 / 2',
    }
  },
  centerColumn: {
    display: 'flex',
    flexDirection: 'column',
    gridGap: theme.spacing(1),
    justifyContent: 'start',
    [theme.breakpoints.up('sm')]: {
      gridArea: '1 / 2 / 2 / 3',
      justifyContent: 'center',
    }
  },
  actions: {
    display: 'flex',
    flexDirection: 'row-reverse',
    gridGap: theme.spacing(1),
    marginTop: theme.spacing(3),
    '& button': {
      padding: '6px 16px',
      width: '100%',
    },
    [theme.breakpoints.up('sm')]: {
      gridArea: '1 / 3 / 2 / 4',
      flexDirection: 'column',
      gridGap: theme.spacing(1),
      justifyContent: shouldTopAlign ? 'start' : 'center',
      marginTop: 0,
      '& button, & a': {
        height: '36px',
        padding: 0
      }
    }
  },
  collapse: {
    display: 'block',
    [theme.breakpoints.up('sm')]: {
      gridArea: '2 / 2 / 3 / 4',
    }
  },
  iconWrapper: {
    alignItems: 'center',
    borderRadius: '50%',
    boxSizing: 'border-box',
    display: 'flex',
    height: '48px',
    justifyContent: 'center',
    padding: '12px',
    width: '48px',
  },
  title: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
  },
  chip: {
    backgroundColor: theme.palette.warning.background,
    border: `1px solid ${theme.palette.warning.border}`,
  },
  expandContainer: {
    display: 'block',
  }
}));

const renderText = (text: React.ReactNode) => {
  if (typeof text === 'string') {
    return (
      <L10nHTML>
        {text}
      </L10nHTML>
    );
  }

  return text;
};
